<template>
    <div class="deliverySchedule">
        <info-banner class="info-banner" position="calendar"/>
        <p v-if="shopEnabled" class="text-center m-0 pt-8 pb-0 px-2">
            {{$t('DELIVERY_SCHEDULE_SHOP')}}
            <router-link :to="{name: 'account.shop'}">
                <span class="underline">
                    {{$t('DELIVERY_SCHEDULE_SHOP_BROWSE')}}
                </span>
            </router-link>
        </p>

        <div class="pause-info-container">
            <div v-if="hasPausedUntilDate && nextDeliveryDate" class="pause-info">
                <p class="mt-0 mb-0" v-html="$t('PAUSE_INFO_MESSAGE', { dateString, nextDeliveryDate })"/>
            </div>
        </div>

        <div v-if="isPaused" class="text-center container mx-auto px-2 md:px-16 lg:px-32 xl:px-64 pb-2">
            <p class="text-center m-0 pt-8 pb-0 px-2">
                <unpause-button :subscription="subscription" />
            </p>
        </div>

        <div v-else class="pb-8"></div>

        <delivery-schedule-address
            v-if="isActive || isPaused"
            :address="address"
            :show-dropdown="showDropdown"
            address-class="px-4 pt-2"/>
        <div v-else class="h-1 lg:h-2"></div>

        <!-- Information banners -->
        <div v-if="shiftedDeliveries[0]" class="account-banner">
            <p v-for="(item, index) in shiftedDeliveries"
            :key="index"
            class="account-banner__content"
            v-text="$t('DELIVERY_SCHEDULE_SHIFTED_DELIVERY', {oldDate: formattedDate(item.before_date), newDate: formattedDate(item.after_date)})">
            </p>
        </div>
        <div v-if="isBondHolder && remainingFlowerbondBoxes !== 0" class="text-center -mt-1 px-2">
            <p>{{$t('DELIVERY_SCHEDULE_FLOWER_BOND_FREE_DELIVERY', {firstName: firstName})}}</p>
            <p class="font-normal">{{$t('DELIVERY_SCHEDULE_FLOWER_BOND_COUNTER', {remaining: remainingFlowerbondBoxes, box: remainingFlowerbondBoxes === 1 ? 'box' : 'boxes'})}}</p>
        </div>
        <div v-if="remainingFlowerBondSingleGiftMultiDeliveries" class="text-center mb-8 px-2">
            <p class="font-normal">{{$t('DELIVERY_SCHEDULE_SINGLE_GIFT_COUNTER', {remaining: remainingFlowerBondSingleGiftMultiDeliveries, credit: remainingFlowerBondSingleGiftMultiDeliveries === 1 ? 'credit' : 'credits'})}}</p>
        </div>
        <div v-if="!isBondHolder && remainingPrimaryNonBond" class="text-center -mt-1 mb-8 px-2">
            <p v-if="discountPrimaryNonBond !== '100%'" class="font-normal">{{$t('DELIVERY_SCHEDULE_FLOWER_NON_BOND_COUNTER', {remaining: remainingPrimaryNonBond, amount: discountPrimaryNonBond, box: getBoxCopyText(remainingPrimaryNonBond)})}}</p>
            <p v-if="discountPrimaryNonBond === '100%'" class="font-normal">{{$t('DELIVERY_SCHEDULE_FLOWER_NON_BOND_FULL_DISCOUNT', {remaining: remainingPrimaryNonBond, box: getBoxCopyText(remainingPrimaryNonBond)})}}</p>
        </div>

        <!-- Calendar -->
        <div class="pt-2">
            <div class="deliverySchedule__container">
                <div class="mx-auto w-full deliverySchedule__header">
                    <calendar-header/>
                </div>
                <div v-if="isDeliveryDaySelected" class="h-10">
                    <div class="deliverySchedule__card deliverySchedule__card--onPaper"></div>
                </div>
            </div>
            <div class="divider--container" id="portrait-scroll-hook">
                <div class="divider-white--top"></div>
            </div>

            <div class="background--paper py-6">
                <div class="deliverySchedule__container">
                    <div class="deliverySchedule__containerCalendar">
                        <Calendar
                            class="calendar-container"
                            :deliverable-days="allDeliveries"
                            :lockTooltipText="$t('CALENDAR_TOO_LATE_TO_MAKE_CHANGES')"
                            :allowMonthToggling="enableMonthSwitching"/>
                    </div>

                    <delivery-card-container
                        v-if="isDeliveryDaySelected"
                        :address="address"
                        class="deliverySchedule__card--onPaper"/>
                </div>
                <div id="landscape-scroll-hook"/>
            </div>

            <div class="divider--container">
                <div class="divider-white--bottom"></div>
            </div>
        </div>

        <div>
            <div v-if="isActive || isPaused">
                <div v-if="isDeliveryDaySelected" class="h-3"></div>

                <div v-if="!isDeliveryDaySelected">
                    <p class="text-center pt-6 pb-0 px-2" v-text="$t('DELIVERY_SCHEDULE_CHOOSE_A_DELIVERY')">
                    </p>
                </div>

                <delivery-card-container
                    v-else
                    :address="address"
                    class="lg:hidden mx-auto w-full clearfix mb-2"/>
            </div>

            <!-- Below the fold content -->
            <div class="block lg:hidden">
                <div class="divider--container"><div class="divider-white--top"></div></div>
                <div class="background--paper h-6"></div>
                <div class="divider--container"><div class="divider-white--bottom"></div></div>
            </div>

            <manage-delivery
                v-if="subscription"
                :key="subscription ? subscription.id : _uid"
                :show-dropdown="showDropdown"/>

            <div v-if="shopEnabled">
                <p class="text-center underline p-4 cursor-pointer" @click="openSummaryModal" v-text="$t('SHOP_CALENDAR_ORDER_SUMMARY')"></p>
            </div>

            <div class="block text-center">
                <recommendations
                    :className="'calendar'"
                    :headerText="$t('RECOMMENDATIONS_CALENDAR')"
                    :numItems="4">
                </recommendations>

                <button
                    class="product__info__button product__info__button__green"
                    v-text="$t('RECOMMENDATIONS_CALENDAR_SHOP_BUTTON')"
                    @click="goToShop">
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import isEmpty from 'lodash/isEmpty';
import { Calendar } from '@freddiesflowers/frontend-component-library';
import { formatDate, addWeeks, isDateAfter } from '@/helpers/DateHelpers';
import * as Dates from '@/constants/Dates';
import { turnScrollOff } from '@/helpers/ScrollHelpers.js';
import { SUBSCRIPTION_STATUS_ACTIVE, SUBSCRIPTION_STATUS_PAUSED } from '@/constants/Subscription';
import ManageDelivery from '@/components/partials/account/my-delivery-schedule/ManageDelivery';
import DeliveryScheduleAddress from '@/components/partials/account/my-delivery-schedule/DeliveryScheduleAddress';
import CalendarHeader from '@/components/partials/account/my-delivery-schedule/calendar/CalendarHeader';
import DeliveryCardContainer from '@/components/partials/account/my-delivery-schedule/deliveryCard/DeliveryCardContainer';
import Recommendations from '../../components/partials/account/shop/Recommendations.vue';
import UnpauseButton from '../../components/partials/account/my-delivery-addresses/UnpauseButton.vue';
import InfoBanner from '@/components/partials/account/InfoBanner.vue';

export default {
    name: 'DeliverySchedule',
    components: {
        DeliveryScheduleAddress,
        DeliveryCardContainer,
        ManageDelivery,
        CalendarHeader,
        Recommendations,
        UnpauseButton,
        InfoBanner,
        Calendar,
    },
    mounted() {
        this.fetchData();

        this.$root.$on('calendar-date-changed', (payload) => {
            payload.change > 0 ? this.fetchNextMonth() : this.fetchPrevMonth();
        });

        this.$root.$on('delivery-day-selected', (payload) => {
            this.$store.dispatch('scheduledOrder/selectDeliveryDay', payload);
        });
    },
    data() {
        return {
            showSummaryModal: false,
        };
    },
    watch: {
        'subscription.id'() {
            this.$store.dispatch('scheduledOrder/resetScheduledOrders');
            this.fetchDeliveries(true).then(() => {
                if (this.enableMonthSwitching) {
                    this.fetchPrevMonth();
                    this.fetchNextMonth();
                }
            });
        },
    },
    computed: {
        ...mapGetters({
            firstName: 'account/getFirstName',
            activeSubscriptions: 'subscription/getActiveSubscriptions',
            shiftedDeliveries: 'scheduledOrder/getShiftedDeliveries',
            selectedDelivery: 'scheduledOrder/getSelectedDelivery',
            subscription: 'subscription/getActiveSubscription',
            allDeliveries: 'scheduledOrder/getAllDeliveries',
            deliveries: 'scheduledOrder/getDeliveries',
        }),
        address() {
            return this.subscription ? this.$store.getters['address/getAddressById'](this.subscription.delivery_address_id) : {};
        },
        enableMonthSwitching() {
            return process.env.VUE_APP_ENABLE_MONTH_SWITCHING === 'true';
        },
        isActive() {
            return this.subscription && this.subscription.status === SUBSCRIPTION_STATUS_ACTIVE;
        },
        isPaused() {
            return this.subscription && this.subscription.status === SUBSCRIPTION_STATUS_PAUSED;
        },
        isDeliveryDaySelected() {
            return !isEmpty(this.selectedDelivery);
        },
        showDropdown() {
            return this.activeSubscriptions.length > 1;
        },
        shopEnabled() {
            return process.env.VUE_APP_SHOP_ENABLED === 'true' && !this.isPaused;
        },
        hasChristmasDayDelivery() {
            const filterForChristmasDelivery = this.deliveries.filter((delivery) => { return delivery.datetime === '2020-12-25' && delivery.is_undeliverable_date; });
            const hasChristmasDelivery = filterForChristmasDelivery.length;
            return hasChristmasDelivery;
        },
        isBondHolder() {
            const hasBond = this.subscription?.bond_info?.has_bond && this.subscription.bond_info.total_uses > 0;
            return hasBond;
        },
        totalFlowerbondBoxes() {
            const total = this.subscription?.bond_info?.total_uses;
            return total;
        },
        remainingFlowerbondBoxes() {
            const used = this.subscription?.bond_info?.total_used;
            const total = this.subscription?.bond_info?.total_uses;
            const remaining = Number(total - used);
            return remaining;
        },
        remainingPrimaryNonBond() {
            return this.subscription ? Number(this.subscription.primary_coupon_info.primary_coupon_total_remaining) : 0;
        },
        discountPrimaryNonBond() {
            return this.subscription ? this.subscription.primary_coupon_info.primary_coupon_discount : 0;
        },
        remainingFlowerBondSingleGiftMultiDeliveries() {
            return this.subscription ? Number(this.subscription.flower_bond_remaining) : 0;
        },
        hasPausedUntilDate() {
            return this.subscription?.paused_until;
        },
        dateString() {
            return this.hasPausedUntilDate && formatDate(this.hasPausedUntilDate, Dates.DATE_MONTH);
        },
        nextDeliveryDate() {
            if (!this.hasPausedUntilDate) return;
            let formattedPausedDate = this.hasPausedUntilDate && formatDate(this.hasPausedUntilDate, Dates.DASHED_YEAR_MONTH_DAY);
            let upcomingDates = this.allDeliveries?.filter(item => {
                return isDateAfter(item.datetime, formattedPausedDate) && item.delivery_scheduled;
            });
            return upcomingDates[0]?.datetime ? formatDate(upcomingDates[0]?.datetime, Dates.DATE_MONTH) : null;
        },
    },
    methods: {
        ...mapActions({
            fetchDeliveries: 'scheduledOrder/fetchAllDeliveries',
        }),
        handleDeliveriesPaused() {
            this.isActive = false;
            this.fetchData();
        },

        handleDeliveriesResumed() {
            this.isActive = true;
            this.fetchData();
        },

        isSubscriptionActive() {
            this.isActive = this.subscription.status === SUBSCRIPTION_STATUS_ACTIVE;
        },

        openSummaryModal() {
            this.$store.commit('shop/openOrderSummary');
            turnScrollOff(document);
        },

        fetchData() {
            this.$store.dispatch('subscription/fetchAllSubscriptions', true);
            this.$store.dispatch('address/fetchAllAddresses', true);
            this.$store.dispatch('cms/fetchPlans', true);
            this.fetchDeliveries(true).then(() => {
                if (this.enableMonthSwitching) {
                    this.fetchPrevMonth();
                    this.fetchNextMonth();
                }
            });
        },

        fetchPrevMonth() {
            const firstDeliveryDate = this.allDeliveries[0].datetime;
            const prevQuery = {
                from: formatDate(addWeeks(firstDeliveryDate, -5), Dates.DASHED_YEAR_MONTH_DAY),
                to: firstDeliveryDate,
            };
            this.$store.dispatch('scheduledOrder/fetchDeliveriesForDateRange', prevQuery);
        },

        fetchNextMonth() {
            const secondDeliveryDate = this.allDeliveries[this.allDeliveries.length - 1].datetime;
            const nextQuery = {
                to: formatDate(addWeeks(secondDeliveryDate, 6), Dates.DASHED_YEAR_MONTH_DAY),
                from: formatDate(addWeeks(secondDeliveryDate, 1), Dates.DASHED_YEAR_MONTH_DAY),
            };
            this.$store.dispatch('scheduledOrder/fetchDeliveriesForDateRange', nextQuery);
        },

        formattedDate(date) {
            return date && formatDate(date, Dates.DAY_DATE_OF_MONTH);
        },

        goToShop() {
            this.$router.push({ name: 'account.shop' });
        },

        getBoxCopyText(value) {
            return value === 1 ? 'box' : 'boxes';
        },
    },
};
</script>

<style lang="scss" scoped>
    .calendar-container {
        margin: 0 auto;
    }
    .deliverySchedule {
        position: relative;
    }

    .pause-info-container {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
    }

    .pause-info {
        padding: 12px;
        background: #ECE9E8;
        border-radius: 8px;
        width: 343px;
        margin-top: 40px;
        text-align: center;
    }

    @media (min-width: 480px) {
        .pause-info {
            width: 580px;
        }
    }
</style>
